import React, { createContext } from "react";
import { InterviewRoundTab, InterviewSession } from "../../Interface/Index";
import { JobApplication } from "../../Entities/job-application.entity";
import { JobDescription } from "../../Entities/job-description.entity";
import { useDashboardLogic } from "./useDashboardLogic";

interface DashboardContextType {
  enabledInterviewRoundTabs: InterviewRoundTab[];
  setEnabledInterviewRoundTabs: React.Dispatch<React.SetStateAction<InterviewRoundTab[]>>;
  appliedJob: JobApplication | null;
  setAppliedJob: React.Dispatch<React.SetStateAction<JobApplication | null>>;
  jobDescription: JobDescription | null;
  setJobDescription: React.Dispatch<React.SetStateAction<JobDescription | null>>;
  showOnlineInterviewTab: boolean;
  setShowOnlineInterviewTab: React.Dispatch<React.SetStateAction<boolean>>;
  interviewSessions: InterviewSession[];
  setInterviewSessions: React.Dispatch<React.SetStateAction<InterviewSession[]>>;
  enableRounds: (roundId: string, sessionId: string) => void;
  addSession: (interviewSession: InterviewSession) => void;
  addSessionAndEnableRound: (interviewSession: InterviewSession) => void;
  checkLastRound: (sessionId: string) => boolean;
  selectedKey: string;
  setSelectedKey: React.Dispatch<React.SetStateAction<string>>;
  showResultTab: boolean;
  setShowResultTab: React.Dispatch<React.SetStateAction<boolean>>;
  refetchSessionByJobApplicationId: () => Promise<void>;
  updateSession: (sessionId: string, updatedSession: InterviewSession) => void;
  isProcessingNextRound: boolean;
  setIsProcessingNextRound: React.Dispatch<React.SetStateAction<boolean>>;
  initComplete: boolean;
  setInitComplete: React.Dispatch<React.SetStateAction<boolean>>;
}

const DashboardContext = createContext<DashboardContextType>({
  enabledInterviewRoundTabs: [],
  setEnabledInterviewRoundTabs: () => {},
  appliedJob: null,
  setAppliedJob: () => {},
  jobDescription: null,
  setJobDescription: () => {},
  showOnlineInterviewTab: false,
  setShowOnlineInterviewTab: () => {},
  interviewSessions: [],
  setInterviewSessions: () => {},
  enableRounds: () => {},
  addSession: () => {},
  addSessionAndEnableRound: () => {},
  checkLastRound: () => false,
  selectedKey: "",
  setSelectedKey: () => {},
  showResultTab: false,
  setShowResultTab: () => {},
  refetchSessionByJobApplicationId: async () => {},
  updateSession: () => {},
  isProcessingNextRound: false,
  setIsProcessingNextRound: () => {},
  initComplete: false,
  setInitComplete: () => {},
});

const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use the custom hook for all logic and state
  const {
    enabledInterviewRoundTabs,
    setEnabledInterviewRoundTabs,
    appliedJob,
    setAppliedJob,
    jobDescription,
    setJobDescription,
    showOnlineInterviewTab,
    setShowOnlineInterviewTab,
    interviewSessions,
    setInterviewSessions,
    enableRounds,
    addSession,
    addSessionAndEnableRound,
    checkLastRound,
    selectedKey,
    setSelectedKey,
    showResultTab,
    setShowResultTab,
    refetchSessionByJobApplicationId,
    updateSession,
    isProcessingNextRound,
    setIsProcessingNextRound,
    initComplete,
    setInitComplete,
  } = useDashboardLogic();

  const contextValue: DashboardContextType = {
    enabledInterviewRoundTabs,
    setEnabledInterviewRoundTabs,
    appliedJob,
    setAppliedJob,
    jobDescription,
    setJobDescription,
    showOnlineInterviewTab,
    setShowOnlineInterviewTab,
    interviewSessions,
    setInterviewSessions,
    enableRounds,
    addSession,
    addSessionAndEnableRound,
    checkLastRound,
    selectedKey,
    setSelectedKey,
    showResultTab,
    setShowResultTab,
    refetchSessionByJobApplicationId,
    updateSession,
    isProcessingNextRound,
    setIsProcessingNextRound,
    initComplete,
    setInitComplete,
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

export { DashboardContext, DashboardProvider };
